import { Attr, HasMany, Model } from 'spraypaint';
import ApplicationRecord from '../ApplicationRecord';
import Group from './Group';

@Model()
export default class User extends ApplicationRecord {
  static jsonapiType = 'users';

  static async register({
    email,
    password,
    phone,
    firstName,
    lastName,
    addressOne,
    addressTwo,
    city,
    state,
    postCode,
    acceptedTerms,
  }: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    addressOne: string;
    addressTwo: string;
    city: string;
    state: string;
    postCode: number | null;
    phone: string;
    acceptedTerms: boolean;
  }) {
    const user = new User({ email, password });
    user.firstName = firstName;
    user.lastName = lastName;
    user.addressOne = addressOne;
    user.addressTwo = addressTwo;
    user.city = city;
    user.state = state;
    user.postCode = postCode || 0;
    user.phone = phone;
    // Required on create and never echoed back. False is a validation error
    // server-side, not a silent no-op, so do not send a registration without it.
    user.acceptedTerms = acceptedTerms;
    await user.save();
    return user;
  }

  static listHeaders() {
    return [
      {
        text: 'ID',
        value: 'id',
        width: 100,
      },
      {
        text: 'Name',
        value: 'firstName',
      },
      {
        text: 'Email address',
        value: 'email',
      },
      {
        text: 'Active',
        value: 'isActive',
      },
      {
        // Not sortable: the list orders server-side and `is_verified` is not
        // among the endpoint's ordering fields, so offering the header would
        // send a request Django refuses. Use the filter instead.
        text: 'Verified',
        value: 'isVerified',
        sortable: false,
        width: 110,
      },
    ];
  }

  static scopeFactory() {
    return User.includes(['groups']);
  }

  static defaultOptions() {
    return {
      sortBy: ['id'],
      sortDesc: [true],
    };
  }

  /**
   * Admin list filters (P5-04).
   *
   * `is_verified` is a real server-side filter as of P5-03; the values are the
   * strings Django's boolean filter parses. "All" sends nothing at all, which
   * matters because an unknown or empty filter value is a 400.
   */
  static listFilters() {
    return [
      {
        label: 'Verification',
        param: 'is_verified',
        items: [
          { text: 'All users', value: '' },
          { text: 'Verified', value: 'true' },
          { text: 'Not verified', value: 'false' },
        ],
      },
    ];
  }

  @Attr() email: string;
  @Attr() number: number;
  @Attr() phone: string;
  @Attr() firstName: string;
  @Attr() lastName: string;
  @Attr() addressOne: string;
  @Attr() addressTwo: string;
  @Attr() city: string;
  @Attr() state: string;
  @Attr() postCode: number;
  @Attr() isActive: boolean;

  // write only
  @Attr() password: string;

  // Write-only, create-only: the T&Cs checkbox. Never present in a response,
  // and ignored on PATCH - an existing user accepts via TermsAcceptance.
  @Attr() acceptedTerms: boolean;

  // Read-only T&Cs status (P2-06). `termsAcceptanceRequired` is the one to key
  // UI off; do NOT re-derive it by comparing `acceptedTermsVersion` against the
  // current version, which would nag users whose older acceptance is still fine.
  @Attr() termsAcceptanceRequired: boolean;

  @Attr() hasAcceptedCurrentTerms: boolean;

  @Attr() acceptedTermsVersion: string;

  // Read-only verification status (P4-07). The other half of the bid gate's
  // answer: a `verification_required` refusal deliberately does not say which
  // obligation is missing, so pair this with `termsAcceptanceRequired` to word
  // the popup - resend the email, read the terms, or both.
  @Attr() isVerified: boolean;

  // Read-only (P5-03). True when a staff member vouched for the account rather
  // than the bidder proving the address from the emailed link. Shown wherever
  // `isVerified` is: they are not the same claim.
  @Attr() verifiedManually: boolean;

  // Read-only (P5-03). When the account was confirmed; empty until it is.
  @Attr() verifiedAt: string;

  // Read-only (P5-03). Who performed the most recent manual verification, as a
  // plain string. **Empty for a reader without `users.view_user`**, including a
  // bidder reading their own row: that an account was vouched for is theirs to
  // know, which employee did it is internal. Empty too when that staff account
  // has since been deleted, in which case the date still stands. So an empty
  // name is not evidence that nobody overrode this account - read
  // `verifiedManually` for that.
  @Attr() verifiedByName: string;

  // Read-only (P4-09). The bidding deadline as it concerns *this* reader:
  // `null`, an ISO date (the LAST day of the window, inclusive), or the literal
  // `'closed'`. Do not read it directly - `gracePeriod()` in
  // `@/accountNotices` turns it into something discriminated, and is the one
  // place that knows about the magic string and about parsing the date safely.
  //
  // `null` means "no deadline concerns you", NOT "no deadline exists": it also
  // covers a complete account and a staff account. An admin view of the
  // site-wide date would need its own field.
  @Attr() gracePeriod: string | null;

  @Attr() currentPassword: string;

  @HasMany() groups: Group[];

  changePassword(currentPassword: string, newPassword: string) {
    const user = new User({ id: this.id });
    user.isPersisted = true;
    user.password = newPassword;
    user.currentPassword = currentPassword;
    return user.save();
  }
}
